import { array, bool, func, object, oneOf, number, string } from 'prop-types'
import { kuiperClassNames, renderIcon } from '../../utils'
import ActiveFilters from '../ActiveFilters'
import ActiveHeader from '../ActiveHeader'
import SearchBar from '../SearchBar'
import SearchResults from '../SearchResults'
import { stopped } from '../utils'

const isFilled = (searchTerm) => Boolean(searchTerm.length)

const FilterBrowserView = ({
  currentPath,
  disabled,
  disabledValues,
  enabledFilters,
  filters,
  filterTree,
  filterValues,
  focused,
  highlightedFilterId,
  lastPath,
  loading,
  onClearFilters,
  onClose,
  onDisableAll,
  onDisableFilter,
  onFilterMouseMove,
  onFilterRemove,
  onFilterSelect,
  onFilterValueChange,
  onFocus,
  onKeyDown,
  onKeyUp,
  onPathChange,
  onSearch,
  onSearchBarFocus,
  results,
  scrollToIndex,
  searchTerm,
  theme,
  track,
  value,
  ...otherProps
}) => {
  const closeAndTrack = () => {
    if (typeof track === 'function') {
      track('Close FilterBar')
    }
    onClose()
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={kuiperClassNames('filter-bar', theme, {
        focused,
        filled: isFilled(searchTerm),
        withFilters: value.length,
        tracking: 'filter-bar',
      })}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      tabIndex="-1"
    >
      <div className={kuiperClassNames('search-header')}>
        <SearchBar
          focused={focused}
          onFocus={onSearchBarFocus}
          onSearch={onSearch}
          searchTerm={searchTerm}
          theme={theme}
          track={track}
          {...otherProps}
        />
        {onClose ? (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <div
            className={kuiperClassNames('closer')}
            onClick={() => closeAndTrack()}
            tabIndex={0}
            role="button"
          >
            {renderIcon('drawer-close', {
              height: 16,
              width: 16,
              ariaLabel: 'Close filter drawer',
            })}
          </div>
        ) : null}
      </div>

      <div className={kuiperClassNames('filter-tree')}>
        <SearchResults
          currentPath={currentPath}
          filterTree={filterTree}
          highlightedFilterId={highlightedFilterId}
          lastPath={lastPath}
          loading={loading}
          onFilterMouseMove={onFilterMouseMove}
          onFilterRemove={stopped(onFilterRemove)}
          onFilterSelect={stopped(onFilterSelect)}
          onPathChange={stopped(onPathChange)}
          results={results}
          scrollToIndex={scrollToIndex}
          searchTerm={searchTerm}
          theme={theme}
          value={value}
        />
      </div>

      <ActiveHeader
        disabled={disabled}
        enabledFilters={enabledFilters}
        onClearFilters={onClearFilters}
        onDisableAll={onDisableAll}
        theme={theme}
        value={value}
      />

      <ActiveFilters
        disabled={disabled}
        disabledValues={disabledValues}
        enabledFilters={enabledFilters}
        filters={filters}
        filterValues={filterValues}
        onChange={onFilterValueChange}
        onClearFilters={onClearFilters}
        onDisableAll={onDisableAll}
        onDisableFilter={onDisableFilter}
        onFilterRemove={stopped(onFilterRemove)}
        theme={theme}
        value={value}
      />
    </div>
  )
}
FilterBrowserView.displayName = 'Kuiper: FilterBrowserView ← FilterBar '

FilterBrowserView.propTypes = {
  currentPath: array,
  disabled: bool,
  disabledValues: array,
  enabledFilters: array,
  filters: array,
  filterTree: array,
  filterValues: object,
  focused: bool,
  highlightedFilterId: string,
  lastPath: array,
  loading: bool,
  onClearFilters: func,
  onClose: func,
  onDisableAll: func,
  onDisableFilter: func,
  onFilterMouseMove: func,
  onFilterRemove: func,
  onFilterSelect: func,
  onFilterValueChange: func,
  onFocus: func,
  onKeyDown: func,
  onKeyUp: func,
  onPathChange: func,
  onSearch: func,
  onSearchBarFocus: func,
  results: array,
  scrollToIndex: number,
  searchTerm: string,
  theme: oneOf(['dark', 'light']),
  track: func,
  value: array,
}

export default FilterBrowserView
